import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ShieldLogo } from "../components/landing/Logo";
import { 
  Shield, AlertTriangle, CheckCircle2, Clock, FileText,
  Download, Home, RefreshCw, Loader2, X, Filter
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Severity badge colors
const SEVERITY_STYLES = {
  CRITICAL: "bg-red-500 text-white",
  WARNING: "bg-amber-500 text-black",
  INFO: "bg-blue-500 text-white"
};

// Incident Card Component
const IncidentCard = ({ incident, onResolve }) => {
  const [showResolve, setShowResolve] = useState(false);
  const [actionTaken, setActionTaken] = useState("");
  const [isResolving, setIsResolving] = useState(false);
  
  const handleResolve = async () => {
    if (!actionTaken.trim()) {
      toast.error("Please describe the action taken");
      return;
    }
    setIsResolving(true);
    await onResolve(incident.incident_id, actionTaken);
    setIsResolving(false);
    setShowResolve(false);
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`glass-card rounded-xl border p-5 ${
        incident.resolved 
          ? "border-lime/30 bg-lime/5" 
          : incident.severity === "CRITICAL"
          ? "border-red-500/30 bg-red-500/5"
          : "border-gold/20"
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${SEVERITY_STYLES[incident.severity] || SEVERITY_STYLES.INFO}`}>
            {incident.severity}
          </span>
          <span className="text-xs text-slate-500">{incident.category}</span>
        </div>
        <div className="flex items-center gap-2">
          {incident.resolved ? (
            <span className="flex items-center gap-1 text-xs text-lime">
              <CheckCircle2 className="w-3 h-3" /> Resolved
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-amber-400">
              <Clock className="w-3 h-3" /> Pending
            </span>
          )}
        </div>
      </div>
      
      {/* Content */}
      <h3 className="font-bold text-white mb-1">{incident.title}</h3>
      <p className="text-sm text-slate-400 mb-3">{incident.description}</p>
      
      {/* Metadata */}
      <div className="flex flex-wrap gap-3 text-xs text-slate-500 mb-3">
        <span>ID: {incident.incident_id}</span>
        <span>•</span>
        <span>{new Date(incident.timestamp).toLocaleString()}</span>
        <span>•</span>
        <span>Location: {incident.location}</span>
      </div>
      
      {/* Recommended Action */}
      {incident.recommended_action && (
        <div className="bg-indigo-700/30 rounded-lg p-3 mb-3">
          <p className="text-xs text-slate-300">
            <span className="text-gold font-bold">Recommended Action:</span> {incident.recommended_action}
          </p>
        </div>
      )}
      
      {/* Resolution */}
      {incident.resolved ? (
        <div className="border-t border-gold/10 pt-3 mt-3">
          <p className="text-xs text-slate-400">
            <span className="text-lime font-bold">Action Taken:</span> {incident.action_taken}
          </p>
          <p className="text-[10px] text-slate-600 mt-1">
            Resolved by {incident.resolved_by} at {new Date(incident.resolved_at).toLocaleString()}
          </p>
        </div>
      ) : (
        <div className="mt-3">
          {!showResolve ? (
            <Button
              onClick={() => setShowResolve(true)}
              variant="outline"
              size="sm"
              className="border-lime/30 text-lime hover:bg-lime/10"
            >
              <CheckCircle2 className="w-3 h-3 mr-1" /> Mark Resolved
            </Button>
          ) : (
            <div className="space-y-2">
              <Textarea
                value={actionTaken}
                onChange={e => setActionTaken(e.target.value)}
                placeholder="Describe the corrective action taken..."
                className="bg-indigo-700/50 border-gold/20 text-white text-sm"
                rows={2}
              />
              <div className="flex gap-2">
                <Button
                  onClick={handleResolve}
                  disabled={isResolving}
                  size="sm"
                  className="bg-lime text-indigo-700"
                >
                  {isResolving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                  Confirm
                </Button>
                <Button
                  onClick={() => setShowResolve(false)}
                  variant="ghost"
                  size="sm"
                  className="text-slate-400"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

// Main Safety Log Page
export default function SafetyLogPage() {
  const { token } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState({ resolved: null, severity: null });
  
  const headers = { Authorization: `Bearer ${token}` };
  
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (filter.resolved !== null) params.resolved = filter.resolved;
      if (filter.severity) params.severity = filter.severity;
      
      const res = await axios.get(`${API}/safety/incidents`, { headers, params });
      setIncidents(res.data.incidents || []);
      setStats(res.data.stats);
    } catch (err) {
      toast.error("Failed to load safety incidents");
    } finally {
      setIsLoading(false);
    }
  }, [token, filter]);
  
  useEffect(() => {
    if (token) fetchData();
  }, [token, fetchData]);
  
  const handleResolve = async (incidentId, actionTaken) => {
    try {
      await axios.put(`${API}/safety/incidents/${incidentId}/resolve`, null, {
        headers,
        params: { action_taken: actionTaken }
      });
      toast.success("Incident marked as resolved");
      fetchData();
    } catch (err) {
      toast.error("Failed to resolve incident");
    }
  };
  
  const handleExportReport = async () => {
    try {
      const res = await axios.get(`${API}/safety/compliance-report`, { headers });
      
      // Create downloadable JSON
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `safety-compliance-report-${new Date().toISOString().split("T")[0]}.json`;
      a.click();
      
      toast.success("Compliance report downloaded");
    } catch (err) {
      toast.error("Failed to generate report");
    }
  };
  
  return (
    <div className="min-h-screen bg-[#040F49]" data-testid="safety-log-page">
      <div className="noise-overlay" />
      
      {/* Header */}
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-indigo-700/90 border-b border-gold/20">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldLogo size="sm" />
            <span className="font-inter text-sm font-bold text-gold tracking-wider">SAFETY INCIDENT LOG</span>
            <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-[10px] font-bold uppercase">
              Compliance
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleExportReport}
              variant="outline"
              size="sm"
              className="border-gold/30 text-gold hover:bg-gold/10 text-xs"
            >
              <Download className="w-3 h-3 mr-1" /> Export Report
            </Button>
            <button onClick={fetchData} className="text-slate-400 hover:text-white">
              <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
            </button>
            <a href="/admin/vision" className="text-slate-400 hover:text-gold text-xs">Vision</a>
            <a href="/" className="text-slate-400 hover:text-white">
              <Home className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
      
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="glass-card rounded-xl border border-gold/20 p-4 text-center">
              <p className="text-2xl font-bold text-gold">{stats.total}</p>
              <p className="text-xs text-slate-400">Total Incidents</p>
            </div>
            <div className="glass-card rounded-xl border border-amber-500/30 p-4 text-center">
              <p className="text-2xl font-bold text-amber-400">{stats.unresolved}</p>
              <p className="text-xs text-slate-400">Unresolved</p>
            </div>
            <div className="glass-card rounded-xl border border-red-500/30 p-4 text-center">
              <p className="text-2xl font-bold text-red-400">{stats.critical_unresolved}</p>
              <p className="text-xs text-slate-400">Critical Pending</p>
            </div>
          </div>
        )}
        
        {/* Filters */}
        <div className="glass-card rounded-xl border border-gold/20 p-4 mb-6">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-xs text-slate-400">Filters:</span>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setFilter({ ...filter, resolved: null })}
                className={`px-3 py-1 rounded text-xs ${filter.resolved === null ? "bg-gold text-indigo-700" : "bg-indigo-700/50 text-slate-300"}`}
              >
                All
              </button>
              <button
                onClick={() => setFilter({ ...filter, resolved: false })}
                className={`px-3 py-1 rounded text-xs ${filter.resolved === false ? "bg-gold text-indigo-700" : "bg-indigo-700/50 text-slate-300"}`}
              >
                Pending
              </button>
              <button
                onClick={() => setFilter({ ...filter, resolved: true })}
                className={`px-3 py-1 rounded text-xs ${filter.resolved === true ? "bg-gold text-indigo-700" : "bg-indigo-700/50 text-slate-300"}`}
              >
                Resolved
              </button>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setFilter({ ...filter, severity: null })}
                className={`px-3 py-1 rounded text-xs ${!filter.severity ? "bg-gold text-indigo-700" : "bg-indigo-700/50 text-slate-300"}`}
              >
                Any Severity
              </button>
              <button
                onClick={() => setFilter({ ...filter, severity: "CRITICAL" })}
                className={`px-3 py-1 rounded text-xs ${filter.severity === "CRITICAL" ? "bg-red-500 text-white" : "bg-red-500/20 text-red-400"}`}
              >
                Critical
              </button>
              <button
                onClick={() => setFilter({ ...filter, severity: "WARNING" })}
                className={`px-3 py-1 rounded text-xs ${filter.severity === "WARNING" ? "bg-amber-500 text-black" : "bg-amber-500/20 text-amber-400"}`}
              >
                Warning
              </button>
            </div>
          </div>
        </div>
        
        {/* Incidents List */}
        {isLoading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gold mx-auto mb-3" />
            <p className="text-slate-400">Loading incidents...</p>
          </div>
        ) : incidents.length === 0 ? (
          <div className="glass-card rounded-xl border border-gold/20 p-12 text-center">
            <Shield className="w-12 h-12 text-lime mx-auto mb-4" />
            <h3 className="font-bold text-white mb-2">No Safety Incidents</h3>
            <p className="text-sm text-slate-400">
              Great news! No safety incidents match your filters.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {incidents.map((incident, idx) => (
              <IncidentCard key={idx} incident={incident} onResolve={handleResolve} />
            ))}
          </div>
        )}
        
        {/* Privacy Notice */}
        <div className="mt-8 text-center">
          <p className="text-xs text-slate-600">
            All incidents auto-logged from Vision analysis • 100% Privacy Compliant • No video stored
          </p>
        </div>
      </div>
    </div>
  );
}
